import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { getBrandLocationString } from "@faire/retailer-visitor-shared/consts/getBrandLocationString";
import { brandPage } from "@faire/retailer-visitor-shared/paths/index";
import { useSettingLoggedOutHomepageRedesignV2 } from "@faire/retailer-visitor-shared/settings/useSettingLoggedOutHomepageRedesignV2";
import * as animationStyles from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/__internal__/Animation/animation.css";
import * as carouselStyles from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/__internal__/Carousel/carousel.css";
import * as linkStyles from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/__internal__/Link/link.css";
import { LoggedOutHomeImage } from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/__internal__/LoggedOutHomeImage/LoggedOutHomeImage";
import * as componentStyles from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/CategoryBestBrandsBanner/__internal__/styles.css";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { logAssert } from "@faire/web--source/common/logging";
import { Carousel } from "@faire/web--source/slate-labs/Carousel/Carousel";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import { trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick } from "@faire/web-api--source/events/loggedOutRetailers/click/loggedOutHomeRedesignCta";
import { trackLoggedOutRetailersLoggedOutHomeRedesignView } from "@faire/web-api--source/events/loggedOutRetailers/view/loggedOutHomeRedesign";
import classNames from "classnames";
import React from "react";
var BrandTile = function(param) {
    var brand = param.brand, pauseScroll = param.pauseScroll, resumeScroll = param.resumeScroll;
    var homepageRedesignVariant = useSettingLoggedOutHomepageRedesignV2();
    var strictLocalize = useStrictLocalization().strictLocalize;
    if (!(brand === null || brand === void 0 ? void 0 : brand.image)) {
        return null;
    }
    var image = brand.image, name = brand.name, based_in = brand.based_in, description = brand.description, token = brand.token, based_in_city = brand.based_in_city, based_in_state = brand.based_in_state, based_in_country = brand.based_in_country;
    logAssert(!!token, "brand token is required for brand tile to link to brand page");
    var _getBrandLocationString;
    var brandLocation = (_getBrandLocationString = getBrandLocationString({
        city: based_in_city,
        state: based_in_state,
        country: based_in_country
    }, strictLocalize)) !== null && _getBrandLocationString !== void 0 ? _getBrandLocationString : based_in;
    return /*#__PURE__*/ _jsx(Link, {
        to: brandPage(token),
        className: classNames(animationStyles.animationHoverTarget, linkStyles.linkParent),
        onMouseEnter: pauseScroll,
        onMouseLeave: resumeScroll,
        onClick: function() {
            trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick("category_best_brands_banner-brand-tile", token !== null && token !== void 0 ? token : "", "".concat(homepageRedesignVariant));
        },
        children: /*#__PURE__*/ _jsxs(Column, {
            gap: 4,
            className: componentStyles.brandTile,
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: animationStyles.imageContainer,
                    children: /*#__PURE__*/ _jsx(LoggedOutHomeImage, {
                        responsiveImage: {
                            mobileAndAbove: image
                        },
                        optimizeImageOptions: {
                            mobileAndAbove: {
                                width: componentStyles.BRAND_TILE_WIDTH,
                                height: componentStyles.BRAND_TILE_WIDTH
                            },
                            xLargeAndAbove: {
                                width: componentStyles.XLARGE_BRAND_TILE_WIDTH,
                                height: componentStyles.XLARGE_BRAND_TILE_WIDTH
                            }
                        },
                        className: animationStyles.imageZoom,
                        altText: name
                    })
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    size: 4
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraphSansMedium",
                    maxLines: 1,
                    children: /*#__PURE__*/ _jsx("span", {
                        className: classNames(animationStyles.underlineText, linkStyles.link),
                        children: name
                    })
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraphSansRegular",
                    color: Core.text.subdued,
                    maxLines: 1,
                    children: brandLocation
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraphSansRegular",
                    maxLines: 2,
                    style: {
                        height: "40px",
                        paddingRight: "".concat(getSpacing("1x"))
                    },
                    children: description
                })
            ]
        })
    });
};
export var CategoryBestBrandsWithCTA = function(param) {
    var selectedCategory = param.selectedCategory, pauseScroll = param.pauseScroll, resumeScroll = param.resumeScroll, className = param.className;
    var homepageRedesignVariant = useSettingLoggedOutHomepageRedesignV2();
    var carouselRef = useDoActionWhenVisible(function() {
        return trackLoggedOutRetailersLoggedOutHomeRedesignView("category_best_brands_banner-category-carousel", "".concat(homepageRedesignVariant));
    });
    var brands = selectedCategory.large_brand_tiles;
    return /*#__PURE__*/ _jsxs(Column, {
        className: classNames(animationStyles.animatedContainerParent, className),
        ref: carouselRef,
        children: [
            /*#__PURE__*/ _jsx(Spacer, {
                height: {
                    mobileAndAbove: "4x",
                    desktopAndAbove: "3x"
                }
            }),
            /*#__PURE__*/ _jsx(ResponsiveContentV2, {
                desktopAndAbove: /*#__PURE__*/ _jsx(Row, {
                    gap: "1x",
                    children: brands.map(function(brand, index) {
                        return /*#__PURE__*/ _jsx(BrandTile, {
                            brand: brand,
                            pauseScroll: pauseScroll,
                            resumeScroll: resumeScroll
                        }, index);
                    })
                }),
                tabletAndBelow: /*#__PURE__*/ _jsx(Carousel, {
                    items: brands,
                    renderItem: function(brand) {
                        return /*#__PURE__*/ _jsx(BrandTile, {
                            brand: brand
                        });
                    },
                    freeFlow: true,
                    showNavigationArrows: false,
                    loop: false,
                    className: carouselStyles.carousel,
                    onVisible: function() {
                        trackLoggedOutRetailersLoggedOutHomeRedesignView("category_best_brands_banner-category-carousel-onscroll", "".concat(homepageRedesignVariant));
                    }
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: {
                    mobileAndAbove: "6x",
                    desktopAndAbove: "5x"
                }
            }),
            /*#__PURE__*/ _jsx(Button, {
                style: {
                    width: "fit-content"
                },
                variant: "secondary",
                linkTo: selectedCategory === null || selectedCategory === void 0 ? void 0 : selectedCategory.category_cta_link,
                className: "hover:!bg-core-surface-default !bg-transparent",
                onMouseEnter: pauseScroll,
                onMouseLeave: resumeScroll,
                onClick: function() {
                    var _selectedCategory_category_cta_link;
                    trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick("category_best_brands_banner-cta", (_selectedCategory_category_cta_link = selectedCategory === null || selectedCategory === void 0 ? void 0 : selectedCategory.category_cta_link) !== null && _selectedCategory_category_cta_link !== void 0 ? _selectedCategory_category_cta_link : "", "".concat(homepageRedesignVariant));
                },
                children: selectedCategory === null || selectedCategory === void 0 ? void 0 : selectedCategory.category_cta_link_text
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: {
                    mobileAndAbove: "6x",
                    desktopAndAbove: "8x"
                }
            })
        ]
    });
};
